<script setup lang="ts">
import { computed, useSlots } from 'vue'

import PrimeButton from 'primevue/button'

const props = defineProps({
  label: {
    type: String,
    default: '',
  },
  variant: {
    type: String,
    default: 'primary',
  },
  outlined: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  disableWhenLoading: {
    type: Boolean,
    default: true,
  },
  icon: {
    type: String,
    default: '',
  },
  iconPosition: {
    type: String as () => 'left' | 'right',
    default: 'left',
  },
  block: {
    type: Boolean,
    default: true,
  },
  buttonClass: {
    type: String,
    default: '',
  },
})

const slots = useSlots()
const isDisabled = computed(() => (props.disableWhenLoading && props.loading) || props.disabled)
const hasDefaultSlot = computed(() => Boolean(slots.default))

const buttonClasses = computed(() => {
  const classes = ['!rounded-xl', 'flex', 'items-center', 'justify-center', 'gap-1']
  if (isDisabled.value) {
    classes.push('!cursor-not-allowed')
  }
  if (props.block) {
    classes.push('w-full')
  }
  if (props.buttonClass) {
    classes.push(props.buttonClass)
  }
  return classes
})
</script>

<template>
  <PrimeButton
    :class="buttonClasses"
    :variant="outlined ? 'outlined' : undefined"
    :loading="false"
    size="small"
    :disabled="isDisabled"
    :severity="variant"
  >
    <i v-if="loading" class="pi pi-spinner app-button-spinner" />

    <template v-if="icon && iconPosition === 'right' && !loading">
      <i :class="`${icon}`" />
    </template>

    <slot name="icon-start" v-if="!loading && !icon"></slot>

    <slot v-if="hasDefaultSlot"></slot>
    <template v-else>{{ label }}</template>

    <slot name="icon-end" v-if="!loading && !icon"></slot>

    <template v-if="icon && iconPosition === 'right' && !loading">
      <i :class="`${icon}`" />
    </template>
  </PrimeButton>
</template>

<style scoped>
:deep(.p-button) {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

:deep(.p-button:disabled),
:deep(.p-button.p-disabled) {
  cursor: not-allowed !important;
  pointer-events: auto;
}

.app-button-spinner {
  animation: app-button-spin 0.8s linear infinite;
}

@keyframes app-button-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
